import { useMemo } from 'react';
import { FilterOption } from '@elementary/common-ui/components';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import {
  getTreeFilterOptionsQuery,
  getTestFilterOptionsQuery,
} from '@elementary/common-ui/services/savedViewsApi';
import {
  TreeFilterOptions,
  TestFilterOptions,
} from '@elementary/common-ui/types/savedViewsTypes';
import { SAVED_VIEWS } from '@elementary/common-ui/constants/queryKeys';
import { ResourceFilterProperty } from '../../shared/types';

const mapParseValueIntoLabelValue = (
  items: { value: string; count: number }[],
) =>
  items.map((item) => ({
    value: item.value,
    label: item.value,
  }));

type Props = {
  environmentId: string;
};

const useResourceFilterOptions = ({ environmentId }: Props) => {
  const {
    data: assetFilterOptionsData,
    error: assetError,
    isLoading: isAssetLoading,
    isFetching: isAssetFetching,
  } = useGraphqlQuery<{ treeFilterOptions: TreeFilterOptions }>({
    queryKey: [SAVED_VIEWS, 'asset-filter-options', environmentId],
    query: getTreeFilterOptionsQuery,
    variables: { filters: null },
    enabled: !!environmentId,
    envId: environmentId,
  });

  const {
    data: testFilterOptionsData,
    error: testError,
    isLoading: isTestLoading,
    isFetching: isTestFetching,
  } = useGraphqlQuery<{ testFilterOptions: TestFilterOptions }>({
    queryKey: [SAVED_VIEWS, 'test-filter-options', environmentId],
    query: getTestFilterOptionsQuery,
    variables: { testFilters: null, assetFilters: null },
    enabled: !!environmentId,
    envId: environmentId,
  });

  const filterOptions: FilterOption[] = useMemo(() => {
    const options: FilterOption[] = [];

    const assetData = assetFilterOptionsData?.treeFilterOptions;
    if (assetData) {
      if (assetData.names?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_NAME,
          propertyLabel: 'Asset Name',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.names),
        });
      }

      if (assetData.tags?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_TAG,
          propertyLabel: 'Asset Tag',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.tags),
        });
      }

      if (assetData.owners?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_OWNER,
          propertyLabel: 'Asset Owner',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.owners),
        });
      }

      if (assetData.subscribers?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_SUBSCRIBER,
          propertyLabel: 'Asset Subscriber',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.subscribers),
        });
      }

      // TODO: Re-enable when critical asset data is supported in the backend
      // if (assetData.isCritical?.length) {
      //   options.push({
      //     property: ResourceFilterProperty.ASSET_IS_CRITICAL,
      //     propertyLabel: 'Is Critical Asset',
      //     options: mapParseValueIntoLabelValue(assetData.isCritical),
      //   });
      // }

      if (assetData.paths?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_PATH,
          propertyLabel: 'Asset Path',
          options: mapParseValueIntoLabelValue(assetData.paths),
        });
      }

      if (assetData.databaseNames?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_DATABASE_NAME,
          propertyLabel: 'Database',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.databaseNames),
        });
      }

      if (assetData.schemaNames?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_SCHEMA_NAME,
          propertyLabel: 'Schema',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.schemaNames),
        });
      }

      if (assetData.tableNames?.length) {
        options.push({
          property: ResourceFilterProperty.ASSET_TABLE_NAME,
          propertyLabel: 'Table',
          multiselect: true,
          options: mapParseValueIntoLabelValue(assetData.tableNames),
        });
      }
    }

    const testData = testFilterOptionsData?.testFilterOptions;
    if (testData) {
      if (testData.names?.length) {
        options.push({
          property: ResourceFilterProperty.TEST_NAME,
          propertyLabel: 'Test Name',
          multiselect: true,
          options: mapParseValueIntoLabelValue(testData.names),
        });
      }

      if (testData.tags?.length) {
        options.push({
          property: ResourceFilterProperty.TEST_TAG,
          propertyLabel: 'Test Tag',
          multiselect: true,
          options: mapParseValueIntoLabelValue(testData.tags),
        });
      }

      if (testData.owners?.length) {
        options.push({
          property: ResourceFilterProperty.TEST_OWNER,
          propertyLabel: 'Test Owner',
          multiselect: true,
          options: mapParseValueIntoLabelValue(testData.owners),
        });
      }
    }

    return options;
  }, [assetFilterOptionsData, testFilterOptionsData]);

  return {
    filterOptions,
    error: assetError || testError,
    isLoading: isAssetLoading || isTestLoading,
    isFetching: isAssetFetching || isTestFetching,
  };
};

export default useResourceFilterOptions;
